import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  X,
  RefreshCw,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  UserPlus,
  Pencil,
  Trash2,
  CalendarDays,
  Filter,
  ShieldCheck,
  AlertTriangle,
  Loader2,
  Mail,
  Briefcase,
} from 'lucide-react';
import { storage } from '../../Shared/Utils/authStorage';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import Pagination from '../../Shared/Pagination/Pagination';
import { PaginationResponse } from '../../Shared/Pagination/PaginationResponse';
import {
  deleteRecruiter,
  getCompanyRecruitersFiltered,
} from '../Services/RecruiterService';
import { RecruiterResponse, RecruiterStats } from '../Types/RecruiterTypes';
import RecruiterFormModal from '../Components/RecruiterFormModal';

const PAGE_SIZE = 8;
const ROLE_OPTIONS = [
  { label: 'All Roles', value: '' },
  { label: 'Company Admin', value: 'ADMIN' },
  { label: 'Recruiter', value: 'MEMBER' },
];
const STATUS_OPTIONS = ['ALL', 'ACTIVE', 'DISABLED'];

const emptyStats: RecruiterStats = { total: 0, admins: 0, active: 0, disabled: 0 };

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatCard = ({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
    <div className="flex items-center justify-between">
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${accent}`}>{icon}</span>
      <span className="text-2xl font-black text-slate-100">{value}</span>
    </div>
    <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
  </div>
);

const RecruiterManagementPage = () => {
  const [recruiters, setRecruiters] = useState<RecruiterResponse[]>([]);
  const [pagination, setPagination] =
    useState<PaginationResponse<RecruiterResponse> | null>(null);
  const [stats, setStats] = useState<RecruiterStats>(emptyStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [role, setRole] = useState('');
  const [status, setStatus] = useState('ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingRecruiter, setEditingRecruiter] = useState<RecruiterResponse | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<RecruiterResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  const userId = storage.getUserId() ?? '';

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchRecruiters = useCallback(async () => {
    if (!userId) {
      setError('No user session found. Please log in again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await getCompanyRecruitersFiltered(
        userId,
        {
          search: debouncedSearch || undefined,
          role: role || undefined,
          status: status !== 'ALL' ? status : undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
        page,
        PAGE_SIZE
      );
      if (response.success && response.data) {
        const pageData = response.data.page;
        setRecruiters(pageData.content ?? []);
        setStats(response.data.stats ?? emptyStats);
        setPagination(pageData);
        if (pageData.totalPages > 0 && page >= pageData.totalPages) {
          setPage(pageData.totalPages - 1);
        }
      } else {
        setRecruiters([]);
        setStats(emptyStats);
        setPagination(null);
        setError(response.message || 'Could not load recruiters.');
      }
    } catch (err) {
      console.error('Failed to load recruiters', err);
      setRecruiters([]);
      setStats(emptyStats);
      setPagination(null);
      setError('An unexpected error occurred while loading recruiters.');
    } finally {
      setLoading(false);
    }
  }, [userId, debouncedSearch, role, status, fromDate, toDate, page]);

  useEffect(() => {
    fetchRecruiters();
  }, [fetchRecruiters]);

  // Reset to the first page whenever the active filters change.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, role, status, fromDate, toDate]);

  const resetFilters = () => {
    setRole('');
    setStatus('ALL');
    setFromDate('');
    setToDate('');
    setSearch('');
  };

  const hasActiveFilters =
    Boolean(role) || status !== 'ALL' || Boolean(fromDate) || Boolean(toDate) || Boolean(search);

  const openCreate = () => {
    setModalMode('create');
    setEditingRecruiter(null);
    setModalOpen(true);
  };

  const openEdit = (recruiter: RecruiterResponse) => {
    setModalMode('edit');
    setEditingRecruiter(recruiter);
    setModalOpen(true);
  };

  const handleSaved = (message: string) => {
    setAlert({ variant: 'success', title: 'Success', message, autoCloseMs: 4000, onClose: () => setAlert(null) });
    fetchRecruiters();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await deleteRecruiter(deleteTarget.id);
      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Deleted',
          message: 'Recruiter removed successfully.',
          autoCloseMs: 4000,
          onClose: () => setAlert(null),
        });
        setDeleteTarget(null);
        fetchRecruiters();
      } else {
        setAlert({
          variant: 'error',
          title: 'Delete failed',
          message: response.message || 'Could not remove the recruiter.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch (err) {
      console.error('Failed to delete recruiter', err);
      setAlert({
        variant: 'error',
        title: 'System error',
        message: 'Something went wrong. Please try again.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setDeleting(false);
    }
  };

  const fullName = (recruiter: RecruiterResponse) =>
    `${recruiter.recruiterFirstName ?? ''} ${recruiter.recruiterLastName ?? ''}`.trim() || 'Recruiter';

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Users size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Manage Recruiters</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Invite, update and control access for your recruitment team
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchRecruiters}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={openCreate}
            className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400"
          >
            <UserPlus size={15} /> Invite Recruiter
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Total Recruiters"
          value={stats.total}
          icon={<Users size={16} />}
          accent="border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
        />
        <StatCard
          label="Company Admins"
          value={stats.admins}
          icon={<ShieldCheck size={16} />}
          accent="border-amber-500/30 bg-amber-500/10 text-amber-400"
        />
        <StatCard
          label="Active"
          value={stats.active}
          icon={<CheckCircle2 size={16} />}
          accent="border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
        />
        <StatCard
          label="Disabled"
          value={stats.disabled}
          icon={<XCircle size={16} />}
          accent="border-rose-500/30 bg-rose-500/10 text-rose-400"
        />
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
          <Filter size={14} /> Filters
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
          <div className="relative xl:col-span-2">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, designation..."
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-9 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="relative">
            <ShieldCheck size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-8 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <CalendarDays size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-3 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            />
          </div>

          <div className="relative">
            <CalendarDays size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-3 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option}
              onClick={() => setStatus(option)}
              className={`rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition ${
                status === option
                  ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                  : 'border-slate-800 bg-slate-950 text-slate-400 hover:text-slate-200'
              }`}
            >
              {option === 'ALL' ? 'All Status' : option}
            </button>
          ))}

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="ml-auto flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-rose-400 hover:border-rose-500/40"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {[0, 1].map((idx) => (
            <div key={idx} className="animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
              <div className="mb-4 h-5 w-48 rounded bg-slate-800" />
              <div className="space-y-3">
                {[0, 1, 2].map((row) => (
                  <div key={row} className="h-4 w-full rounded bg-slate-800/70" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 py-16 text-center text-sm font-bold text-rose-400">
          {error}
        </div>
      ) : recruiters.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <span className="mb-2 block text-2xl">👥</span>
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">No recruiters found</p>
          {hasActiveFilters ? (
            <button onClick={resetFilters} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
              Clear filters
            </button>
          ) : (
            <button onClick={openCreate} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
              Invite your first recruiter
            </button>
          )}
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/70 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-800 bg-slate-950 text-cyan-400">
                  <Users size={18} />
                </span>
                <div>
                  <h3 className="text-sm font-black tracking-tight text-slate-100 md:text-base">Recruiter Directory</h3>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {pagination?.totalElements ?? recruiters.length} recruiter{(pagination?.totalElements ?? recruiters.length) === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
              <button
                onClick={openCreate}
                className="flex items-center gap-1.5 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-cyan-400 transition hover:bg-cyan-500/20"
              >
                <UserPlus size={13} /> Invite
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-3">Recruiter</th>
                    <th className="px-5 py-3">Designation</th>
                    <th className="px-5 py-3">Role &amp; Access</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Joined</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recruiters.map((recruiter) => (
                    <tr
                      key={recruiter.id}
                      className="border-b border-slate-800/60 transition last:border-0 hover:bg-slate-800/20"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-slate-800 bg-slate-950 text-xs font-black text-cyan-400">
                            {recruiter.recruiterFirstName?.[0] || '?'}
                            {recruiter.recruiterLastName?.[0] || ''}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-bold text-slate-100">{fullName(recruiter)}</p>
                            {recruiter.recruiterEmail && (
                              <p className="flex items-center gap-1 truncate text-[11px] text-slate-500">
                                <Mail size={11} /> {recruiter.recruiterEmail}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-300">
                          <Briefcase size={13} className="text-slate-500" />
                          {recruiter.designation || '—'}
                        </p>
                      </td>

                      <td className="px-5 py-4">
                        {recruiter.isCompanyAdminUser ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-amber-400">
                            <ShieldCheck size={11} /> Company Admin
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full border border-slate-700 bg-slate-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
                            Recruiter
                          </span>
                        )}
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                            recruiter.recruiterStatus
                              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                              : 'border-slate-700 bg-slate-950 text-slate-400'
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              recruiter.recruiterStatus ? 'bg-emerald-400' : 'bg-slate-500'
                            }`}
                          />
                          {recruiter.recruiterStatus ? 'Active' : 'Disabled'}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                          <CalendarDays size={12} className="text-slate-500" />
                          {formatDate(recruiter.recruiterCreatedAt)}
                        </p>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEdit(recruiter)}
                            className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40"
                          >
                            <Pencil size={12} /> Edit
                          </button>
                          <button
                            onClick={() => setDeleteTarget(recruiter)}
                            className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-rose-400 transition hover:border-rose-500/40"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {pagination && pagination.totalElements > 0 && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
              <Pagination
                page={pagination.page}
                size={pagination.size}
                totalElements={pagination.totalElements}
                totalPages={pagination.totalPages}
                first={pagination.first}
                last={pagination.last}
                hasNext={pagination.hasNext}
                hasPrevious={pagination.hasPrevious}
                setPage={setPage}
              />
            </div>
          )}
        </motion.div>
      )}

      {/* Create / Edit modal */}
      <RecruiterFormModal
        isOpen={modalOpen}
        mode={modalMode}
        recruiter={editingRecruiter}
        currentUserId={userId}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />

      {/* Delete confirmation */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                <AlertTriangle size={20} />
              </span>
              <div>
                <h3 className="text-base font-black tracking-tight text-slate-100">Remove recruiter?</h3>
                <p className="mt-1 text-sm text-slate-400">
                  This will remove{' '}
                  <span className="font-bold text-slate-200">{fullName(deleteTarget)}</span> from your company
                  directory. This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-rose-400 disabled:opacity-50"
              >
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {deleting ? 'Removing' : 'Remove'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default RecruiterManagementPage;
